import { useEffect, useState } from 'react';
import { SERIES, soft } from './charts/chartColors.js';

// T095 — Scan progress + result panel.
//
// Linear/Vercel-inspired premium look:
//   - Glass card with a thin accent line at the top (gradient teal)
//   - Subtle backdrop-blur, dark/light theme aware via .panel utility
//   - Soft pulse dot that breathes (no harsh ping)
//   - Source pills in a fixed-width font with fine-grained transitions
//   - Numbers in a slightly larger display weight for the result line

// T421 — no invented source list.
//
// This was nine hardcoded adapter names, shown while a scan was running if
// the real list had not arrived yet. The instance runs 87 enabled sources,
// so the fallback was not a rough approximation — it was a different scan,
// cycling names that may not even be enabled. A progress panel with no
// names is honest; one with the wrong names is not, and the operator has
// no way to tell which he is looking at.
const FALLBACK_SOURCES = [];

const TYPICAL_DURATION_MS = 15_000;

export default function ScanPanel({ scanning, scanResult, knownSources, autoExplain, progress }) {
  if (scanning) return <ScanProgressPanel knownSources={knownSources} progress={progress} />;
  if (scanResult) return <ScanResultPanel scanResult={scanResult} autoExplain={autoExplain} />;
  return null;
}

// ─── Premium glass card wrapper ────────────────────────────────────────────
// Single accent line at the top + glass background. Border kept subtle so
// the inner content carries the visual weight, not the chrome.
function Card({ children, accent = 'teal' }) {
  const accentBar =
    accent === 'teal'
      ? 'bg-gradient-to-r from-transparent via-brand-500 to-transparent'
      : 'bg-gradient-to-r from-transparent via-emerald-500 to-transparent';
  return (
    <section className="relative mt-6 overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-900/40 backdrop-blur-md"
      style={{ boxShadow: `0 2px 24px -12px ${soft(SERIES.accent, 25)}` }}>
      <div className={`absolute inset-x-0 top-0 h-px opacity-80 ${accentBar}`} />
      {children}
    </section>
  );
}

function ScanProgressPanel({ knownSources, progress }) {
  const sources = (knownSources && knownSources.length > 0 ? knownSources : FALLBACK_SOURCES)
    .map((s) => (typeof s === 'string' ? s : s?.source ?? ''))
    .filter(Boolean);
  // T113 — real progress from the poll, when the worker writes it mid-scan.
  // Until that ships (Part B), these stay 0 and we show NO fabricated counts.
  const liveFound = progress?.totalJobsFound ?? 0;
  const liveDone = progress?.sourcesQueried?.length ?? 0;
  const [startedAt] = useState(() => Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Date.now() - startedAt), 200);
    return () => clearInterval(t);
  }, [startedAt]);

  // T268(8) — this panel used to state two things it did not know.
  //
  // "Now querying <source>" cycled the source list on a 650ms timer, with no
  // relationship to what the worker was doing: a name shown for two-thirds of a
  // second while that adapter might not run for another twenty. And the
  // progress bar's percentage was elapsed/15s, so it climbed at a fixed rate
  // and pinned at 99% for the rest of a slow scan — the classic bar that means
  // nothing, on a page whose whole argument is that its numbers are honest.
  //
  // The worker does report `sourcesQueried` mid-scan, so THAT is the progress:
  // real when it arrives, absent when it does not. Until T113 Part B fills it
  // in for every adapter, an indeterminate bar tells the truth.
  const determinate = liveDone > 0 && sources.length > 0;
  const pct = determinate ? Math.min(100, Math.round((liveDone / sources.length) * 100)) : null;

  return (
    <Card>
      <div className="p-5">
        {/* Top row: label + soft pulse + timer */}
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2 items-center justify-center">
            <span className="absolute inset-0 animate-[pulse_1.8s_ease-in-out_infinite] rounded-full bg-brand-500" />
            <span className="relative h-1.5 w-1.5 rounded-full bg-brand-500"
            style={{ boxShadow: `0 0 8px ${soft(SERIES.accent, 70)}` }} />
          </span>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
            Scanning · {sources.length || '—'} sources
          </p>
          <span className="ml-auto font-mono text-xs tabular-nums text-slate-500">
            {(elapsed / 1000).toFixed(1)}s
          </span>
        </div>

        {/* Hero line */}
        <div className="mt-4">
          <p className="text-xs uppercase tracking-widest text-slate-500">In progress</p>
          <p className="mt-1 font-mono text-lg font-medium text-slate-100">
            Scanning across {sources.length || 'your'} sources
            <span className="ml-0.5 inline-block w-1.5 animate-pulse text-brand-400">·</span>
          </p>
          <p className="mt-1.5 max-w-xl text-[12px] leading-relaxed text-slate-500">
            Fanning out in parallel across ATSes, public RSS feeds, government boards, and remote indexes. Deduping the live stream and scoring it against your hunt preferences as results arrive.
          </p>
          {/* T250 — this counter ticks up throughout a scan and was announced to
              nobody. role="status" is the polite one: progress, not an alert. */}
          {(liveFound > 0 || liveDone > 0) && (
            <p role="status" className="mt-2 font-mono text-xs text-brand-300">
              {liveFound} jobs found · {liveDone}/{sources.length} sources done
            </p>
          )}
        </div>

        {/* The source list, as a list — no fake cursor moving through it. */}
        {sources.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5">
            {sources.slice(0, 8).map((s) => {
              // Reported done by the worker, or unknown. Nothing in between.
              const done = progress?.sourcesQueried?.includes(s);
              return (
                <span
                  key={s}
                  className={
                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-mono text-[10.5px] tracking-tight transition-all duration-300 ' +
                    (done
                      ? 'border border-brand-500/60 bg-brand-500/10 text-brand-200'
                      : 'border border-slate-800 bg-slate-900/30 text-slate-600')
                  }
                >
                  <span className={'h-1 w-1 rounded-full ' + (done ? 'bg-brand-400' : 'bg-slate-700')} />
                  {s}
                </span>
              );
            })}
          </div>
        )}

        {/* Hair-thin progress bar */}
        <div className="mt-5">
          <div
            className="relative h-[3px] w-full overflow-hidden rounded-full bg-slate-800/60"
            role="progressbar"
            aria-label="Scan progress"
            {...(determinate ? { 'aria-valuenow': pct, 'aria-valuemin': 0, 'aria-valuemax': 100 } : {})}
          >
            {determinate ? (
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-brand-600 via-brand-400 to-brand-600 transition-all duration-200"
                style={{ width: `${pct}%` }}
              />
            ) : (
              // Indeterminate: it says "working", which is all we know.
              <div className="absolute inset-y-0 left-0 w-1/3 animate-[scanSweep_1.4s_ease-in-out_infinite] rounded-full bg-gradient-to-r from-transparent via-brand-400 to-transparent motion-reduce:left-0 motion-reduce:w-full motion-reduce:animate-none motion-reduce:opacity-40" />
            )}
          </div>
          <div className="mt-1.5 flex items-baseline justify-between text-[10px] text-slate-600">
            <span>usually ~{Math.round(TYPICAL_DURATION_MS / 1000)}s, longer with many sources</span>
            {determinate && <span className="font-mono tabular-nums">{liveDone}/{sources.length}</span>}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ScanResultPanel({ scanResult, autoExplain }) {
  const [expanded, setExpanded] = useState(false);
  const total = scanResult.totalJobsRaw ?? 0;
  const matched = scanResult.totalJobsFound ?? 0;
  const fresh = scanResult.totalJobsNew ?? 0;
  const sources = scanResult.sourcesQueried ?? [];
  const errorCount = sources.reduce((n, s) => n + (s.errors?.length ?? 0), 0);
  const succeededCount = sources.filter((s) => (s.errors?.length ?? 0) === 0).length;
  const durationS = ((scanResult.durationMs ?? 0) / 1000).toFixed(1);
  const hasAnyMatch = total > 0;
  // T273 — the housekeeping half of a scan. A run that quietly took 40 postings
  // out of the queue looked identical to one that took none; the operator asked
  // for expired adverts to leave the dashboard, which means he has to be able
  // to see it happen.
  const closed = scanResult.totalJobsClosed ?? 0;
  const reopened = scanResult.totalJobsReopened ?? 0;

  return (
    <Card>
      <div className="p-5">
        {/* Top row: small status + meta on the right */}
        <div className="flex items-center gap-2.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500"
          style={{ boxShadow: `0 0 8px ${soft(SERIES.up, 70)}` }} />
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
            Scan complete
          </p>
          <span className="ml-auto text-xs text-slate-400">
            {succeededCount}/{sources.length} sources · <span className="font-mono tabular-nums">{durationS}s</span>
            {errorCount > 0 && (
              <span className="ml-1 text-amber-400">· {errorCount} err</span>
            )}
          </span>
        </div>

        {/* Hero metric trio */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Metric label="New" value={fresh} accent="emerald" />
          <Metric label="Matched" value={matched} accent="brand" />
          <Metric label="Raw" value={total} accent="slate" />
        </div>

        {(closed > 0 || reopened > 0) && (
          <p className="mt-3 text-xs text-slate-400">
            {closed > 0 && (
              <>
                <span className="font-mono tabular-nums text-slate-300">{closed}</span>
                {' '}closed — off the board or past their closing date
              </>
            )}
            {closed > 0 && reopened > 0 && ' · '}
            {reopened > 0 && (
              <>
                <span className="font-mono tabular-nums text-slate-300">{reopened}</span>
                {' '}back — still advertised
              </>
            )}
          </p>
        )}

        {hasAnyMatch && matched === 0 && (
          <p className="mt-3 rounded-lg border border-amber-700/40 bg-amber-950/20 px-3 py-2 text-xs text-amber-200/90">
            ⓘ {total} jobs returned but none matched your filters. Try widening{' '}
            <strong className="text-amber-100">target roles</strong> or{' '}
            <strong className="text-amber-100">target locations</strong>.
          </p>
        )}

        {/* Auto-explain status (when enabled) */}
        {autoExplain && (
          <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/5 px-3 py-1 text-xs text-brand-200">
            <span className="h-1 w-1 rounded-full bg-brand-400" />
            Auto-explained {autoExplain.explained ?? 0}/{autoExplain.total ?? 0} top recs
            {autoExplain.message ? ` · ${autoExplain.message}` : ''}
          </p>
        )}

        {/* Collapsed dev-log toggle */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500 transition hover:text-slate-300"
        >
          <span className={'transition-transform ' + (expanded ? 'rotate-90' : '')}>›</span>
          {expanded ? 'Hide source log' : 'Show source log'}
        </button>

        {expanded && (
          <div className="mt-3 space-y-3">
            {scanResult.appliedFilters && (
              <div className="flex flex-wrap gap-1.5 text-[10.5px]">
                <span className="text-slate-500">filters:</span>
                <Chip label={`countries: ${scanResult.appliedFilters.countries?.length ? scanResult.appliedFilters.countries.join(', ') : 'any'}`} />
                <Chip label={`roles: ${scanResult.appliedFilters.targetRoles?.length ? scanResult.appliedFilters.targetRoles.join(', ') : 'any'}`} />
                <Chip label={`remote: ${scanResult.appliedFilters.remotePreference ?? 'any'}`} />
                {(Array.isArray(scanResult.appliedFilters.seniority)
                  ? scanResult.appliedFilters.seniority.length > 0
                  : !!scanResult.appliedFilters.seniority) && (
                  <Chip label={`seniority: ${
                    Array.isArray(scanResult.appliedFilters.seniority)
                      ? scanResult.appliedFilters.seniority.join(' / ')
                      : scanResult.appliedFilters.seniority
                  }`} />
                )}
              </div>
            )}

            {sources.length > 0 && (
              <ul className="max-h-64 overflow-y-auto rounded-lg border border-slate-800/80 bg-slate-950/40 px-2 py-1.5 text-[10.5px] font-mono leading-relaxed">
                {sources.map((s) => {
                  const hasErr = (s.errors?.length ?? 0) > 0;
                  // T184 — rows the source handed us that we could not map.
                  // Distinct from `errors`: those are failures the adapter
                  // announced, this is quality lost without anyone saying so.
                  const invalid = s.jobsInvalid ?? 0;
                  return (
                    <li key={s.source} className="flex items-baseline justify-between gap-3 border-b border-slate-900 py-1 last:border-b-0">
                      <span className={hasErr || invalid > 0 ? 'text-amber-300' : 'text-slate-300'}>{s.source}</span>
                      <span className="tabular-nums text-slate-400">
                        {s.jobsRaw ?? 0} → {s.jobsFound} / {s.jobsNew}
                        {invalid > 0 ? (
                          <span className="ml-1 text-red-400" title={`${invalid} rows could not be mapped into a job`}>
                            ⨯{invalid}
                          </span>
                        ) : ''}
                        {hasErr ? <span className="ml-1 text-amber-400">!{s.errors.length}</span> : ''}
                        <span className="ml-2 text-slate-600">{s.durationMs}ms</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function Metric({ label, value, accent }) {
  const accentText =
    accent === 'emerald' ? 'text-emerald-300'
    : accent === 'brand' ? 'text-brand-300'
    : 'text-slate-300';
  const accentBg =
    accent === 'emerald' ? 'bg-emerald-500/8 border-emerald-700/30'
    : accent === 'brand' ? 'bg-brand-500/8 border-brand-700/30'
    : 'bg-slate-800/40 border-slate-800';
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${accentBg}`}>
      <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className={`mt-0.5 font-mono text-2xl font-light tabular-nums tracking-tight ${accentText}`}>
        {Intl.NumberFormat().format(value)}
      </p>
    </div>
  );
}

function Chip({ label }) {
  return (
    <span className="rounded-full border border-slate-700/80 bg-slate-900/40 px-2 py-0.5 text-[10px] text-slate-400">
      {label}
    </span>
  );
}
